import { Check, FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type {
  ScheduleItemImportOptions,
  ScheduleItemsImportPreview,
  ScheduleItemsImportResult,
} from '@obraplan/core/application/contracts/schedule-items';
import type {
  DeParaOverride,
  ScheduleItemRowData,
} from '@obraplan/core/domain/schedule/ganttpro/index';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { cn } from '@/lib/cn';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { ImportConferencia, type ChoiceDraft } from './ImportConferencia';
import {
  FILE_KIND_LABEL,
  MAX_IMPORT_ROWS,
  countLines,
  describeFile,
  joinPreview,
  loadScheduleFile,
  prepareImport,
  serviceKey,
  suggestedTowerName,
  withoutDeclinedServices,
  type LoadedScheduleFile,
} from './import-model';

const STEPS = ['Arquivo', 'Conferência', 'Confirmação', 'Resultado'] as const;

const NO_OPTIONS: ScheduleItemImportOptions = {
  createMissingFloors: false,
  createMissingActivities: false,
  createMissingDisciplines: false,
  createMissingServices: false,
};

const NOTHING_DECLINED: ReadonlySet<string> = new Set();

interface PreviewState {
  /** As linhas que geraram esta resposta — a junção é por posição, e precisa delas. */
  readonly rows: readonly ScheduleItemRowData[];
  readonly preview: ScheduleItemsImportPreview | undefined;
  readonly error: string | undefined;
  readonly loading: boolean;
}

export interface ScheduleItemsImportWizardProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * O assistente de importação do cronograma por item (Fase 25, RN-325).
 *
 * Quatro passos — arquivo, conferência, confirmação, resultado — e o mesmo princípio do
 * assistente do cronograma de materiais: **nada é gravado até a confirmação**. O arquivo é
 * lido aqui, no navegador (`import-model.ts`), e o servidor só recebe linhas normalizadas;
 * a conferência é a resposta dele sobre essas linhas contra o cadastro da obra.
 *
 * Criar pavimento, frente, disciplina ou serviço é escolha explícita, e os serviços novos
 * ainda se aprovam um a um (D3): o que o usuário não aprova não é enviado.
 */
export function ScheduleItemsImportWizard({ open, onOpenChange }: ScheduleItemsImportWizardProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canCreateServices = usePermission(PERMISSION.TAKEOFF_MANAGE);
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<LoadedScheduleFile | undefined>(undefined);
  const [readError, setReadError] = useState<string | undefined>(undefined);
  const [reading, setReading] = useState(false);
  const [tower, setTower] = useState('');
  const [overrides, setOverrides] = useState<Readonly<Record<string, DeParaOverride>>>({});
  const [drafts, setDrafts] = useState<Readonly<Record<string, ChoiceDraft>>>({});
  const [options, setOptions] = useState<ScheduleItemImportOptions>(NO_OPTIONS);
  const [declined, setDeclined] = useState<ReadonlySet<string>>(NOTHING_DECLINED);
  const [previewState, setPreviewState] = useState<PreviewState | undefined>(undefined);
  const [result, setResult] = useState<ScheduleItemsImportResult | undefined>(undefined);

  // ── As sugestões de nome: o que a obra já tem ────────────────────────────────────
  // Só torres e disciplinas: a leitura leve, sem as linhas de material (Fase 26).
  const structure = useLive(
    () =>
      open && session !== undefined && activeProjectId !== undefined
        ? container.getProjectOutline.execute({ projectId: activeProjectId }, session)
        : Promise.resolve(undefined),
    [open, session?.userId, activeProjectId],
  );
  const services = useLive(
    () =>
      open && session !== undefined && activeProjectId !== undefined
        ? container.getProjectServices.execute(activeProjectId, session)
        : Promise.resolve(undefined),
    [open, session?.userId, activeProjectId],
  );

  const towerNames = useMemo(
    () =>
      structure !== undefined && structure.ok
        ? structure.value.tree.towers.map((entry) => entry.tower.name)
        : [],
    [structure],
  );
  const disciplineNames = useMemo(
    () =>
      structure !== undefined && structure.ok
        ? structure.value.availableDisciplines.map((discipline) => discipline.name)
        : [],
    [structure],
  );
  const servicesByDiscipline = useMemo(() => {
    const byName = new Map<string, string[]>();
    if (structure === undefined || !structure.ok || services === undefined || !services.ok) {
      return byName;
    }
    const disciplineOf = new Map(
      structure.value.availableDisciplines.map((discipline) => [discipline.id, discipline.name]),
    );
    for (const service of services.value) {
      const discipline = disciplineOf.get(service.disciplineId);
      if (discipline === undefined) continue;
      byName.set(discipline.toLocaleLowerCase('pt-BR'), [
        ...(byName.get(discipline.toLocaleLowerCase('pt-BR')) ?? []),
        service.name,
      ]);
    }
    return byName;
  }, [structure, services]);

  // ── Do arquivo às linhas ─────────────────────────────────────────────────────────
  const prepared = useMemo(
    () => (file === undefined ? undefined : prepareImport(file, tower, overrides)),
    [file, tower, overrides],
  );

  const conferring = step === 1 || step === 2;

  // A prévia: refeita quando as linhas ou as opções mudam, e só enquanto se confere.
  useEffect(() => {
    if (!conferring || prepared === undefined) return;
    if (session === undefined || activeProjectId === undefined) return;

    const rows = prepared.rows;
    if (rows.length === 0) {
      setPreviewState({ rows, preview: undefined, error: undefined, loading: false });
      return;
    }
    if (rows.length > MAX_IMPORT_ROWS) {
      setPreviewState({
        rows,
        preview: undefined,
        error: `O arquivo tem ${String(rows.length)} itens, e o limite é ${String(MAX_IMPORT_ROWS)} por importação.`,
        loading: false,
      });
      return;
    }

    let alive = true;
    // Mantém a resposta anterior na tela enquanto a nova não chega: trocar uma opção não
    // pode fazer a lista de serviços piscar e tirar o foco da caixa que acabou de ser marcada.
    setPreviewState((current) => ({
      rows: current?.rows ?? rows,
      preview: current?.preview,
      error: undefined,
      loading: true,
    }));
    container.previewScheduleItemsImport
      .execute({ projectId: activeProjectId, rows, options }, session)
      .then(
        (answer) => {
          if (!alive) return;
          setPreviewState(
            answer.ok
              ? { rows, preview: answer.value, error: undefined, loading: false }
              : { rows, preview: undefined, error: answer.error.message, loading: false },
          );
        },
        (cause: unknown) => {
          if (!alive) return;
          setPreviewState({
            rows,
            preview: undefined,
            error: cause instanceof Error ? cause.message : 'Não foi possível conferir o arquivo.',
            loading: false,
          });
        },
      );
    return () => {
      alive = false;
    };
  }, [conferring, prepared, options, container, session, activeProjectId]);

  const effectiveDeclined = options.createMissingServices ? declined : NOTHING_DECLINED;
  const preview = previewState?.preview;
  const lines = useMemo(
    () =>
      previewState === undefined || preview === undefined
        ? undefined
        : joinPreview(previewState.rows, preview, effectiveDeclined),
    [previewState, preview, effectiveDeclined],
  );
  const counts = useMemo(() => (lines === undefined ? undefined : countLines(lines)), [lines]);

  // ── Comandos ─────────────────────────────────────────────────────────────────────
  const [confirm, confirmState] = useCommand(
    () =>
      container.confirmScheduleItemsImport.execute(
        {
          projectId: activeProjectId!,
          rows: withoutDeclinedServices(prepared?.rows ?? [], effectiveDeclined),
          options,
        },
        session!,
      ),
    {
      silent: true,
      onSuccess: (value) => {
        setResult(value);
        setStep(3);
      },
    },
  );

  const reset = (): void => {
    setStep(0);
    setFile(undefined);
    setReadError(undefined);
    setReading(false);
    setTower('');
    setOverrides({});
    setDrafts({});
    setOptions(NO_OPTIONS);
    setDeclined(NOTHING_DECLINED);
    setPreviewState(undefined);
    setResult(undefined);
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const chooseFile = (chosen: File): void => {
    setReading(true);
    setReadError(undefined);
    void chosen
      .arrayBuffer()
      .then((buffer) => {
        const loaded = loadScheduleFile(new Uint8Array(buffer), chosen.name);
        if (!loaded.ok) {
          setFile(undefined);
          setReadError(loaded.error);
          return;
        }
        setFile(loaded.value);
        setTower(suggestedTowerName(loaded.value));
        setOverrides({});
        setDrafts({});
        setDeclined(NOTHING_DECLINED);
        setPreviewState(undefined);
      })
      .catch(() => {
        setFile(undefined);
        setReadError('Não consegui ler o arquivo.');
      })
      .finally(() => setReading(false));
  };

  const applyChoices = (): void => {
    const next: Record<string, DeParaOverride> = { ...overrides };
    const remaining: Record<string, ChoiceDraft> = { ...drafts };
    for (const [key, draft] of Object.entries(drafts)) {
      const discipline = draft.disciplineName.trim();
      if (discipline === '') continue;
      const service = draft.serviceName.trim();
      next[key] = { disciplineName: discipline, serviceName: service === '' ? null : service };
      delete remaining[key];
    }
    setOverrides(next);
    setDrafts(remaining);
  };

  const missingServices = preview?.missing.services ?? [];
  const approvedServices = missingServices.filter(
    (service) => !effectiveDeclined.has(serviceKey(service.disciplineName, service.serviceName)),
  ).length;
  const approveAll = (approved: boolean): void => {
    setDeclined(
      approved
        ? NOTHING_DECLINED
        : new Set(missingServices.map((s) => serviceKey(s.disciplineName, s.serviceName))),
    );
  };
  const approve = (key: string, approved: boolean): void => {
    setDeclined((current) => {
      const next = new Set(current);
      if (approved) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const towerReady = file?.kind !== 'GANTTPRO' || tower.trim() !== '';
  const importable = counts === undefined ? 0 : counts.create + counts.update;
  const canConfirm =
    counts !== undefined && previewState?.loading !== true && importable + counts.unchanged > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Importar cronograma por mão de obra</DialogTitle>
          <DialogDescription>
            A planilha só altera a obra depois que você confirmar. Até lá, nada é gravado.
          </DialogDescription>
        </DialogHeader>

        <ol className="mb-4 flex flex-wrap gap-2 text-xs">
          {STEPS.map((label, index) => (
            <li
              key={label}
              aria-current={index === step ? 'step' : undefined}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1',
                index === step
                  ? 'border-(--color-brand) font-medium text-(--color-brand)'
                  : 'border-(--color-border-subtle) text-(--color-content-muted)',
              )}
            >
              {index < step ? (
                <Check className="size-3" aria-hidden="true" />
              ) : (
                `${String(index + 1)}.`
              )}
              {label}
            </li>
          ))}
        </ol>

        <div className="flex flex-col gap-4">
          {/* ── 1. Arquivo ─────────────────────────────────────────────────────── */}
          {step === 0 && (
            <>
              <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-(--color-border-subtle) p-8 text-center">
                <FileSpreadsheet
                  className="size-8 text-(--color-content-muted)"
                  aria-hidden="true"
                />
                <p className="max-w-xl text-sm text-(--color-content-muted)">
                  Escolha o cronograma exportado do GanttPRO em Excel, ou o modelo de cronograma por
                  item (aba &ldquo;Itens&rdquo;). O formato é reconhecido pelo cabeçalho.
                </p>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".xlsx"
                  aria-label="Planilha do cronograma"
                  className="hidden"
                  onChange={(event) => {
                    const chosen = event.target.files?.[0];
                    if (chosen !== undefined) chooseFile(chosen);
                    event.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant={file === undefined ? 'primary' : 'secondary'}
                  disabled={reading}
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload aria-hidden="true" />
                  {reading
                    ? 'Lendo…'
                    : file === undefined
                      ? 'Escolher planilha'
                      : 'Escolher outra planilha'}
                </Button>
              </div>

              {readError !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {readError}
                </p>
              )}

              {file !== undefined && (
                <div className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) p-4">
                  <p className="text-sm">
                    <strong>{file.fileName}</strong>
                    <span className="text-(--color-content-muted)">
                      {' '}
                      · formato detectado: {FILE_KIND_LABEL[file.kind]} · {describeFile(file)}
                    </span>
                  </p>

                  {file.kind === 'GANTTPRO' ? (
                    <>
                      <datalist id="wizard-towers">
                        {towerNames.map((name) => (
                          <option key={name} value={name} />
                        ))}
                      </datalist>
                      <Field
                        label="Nome da torre"
                        htmlFor="wizard-tower"
                        hint="O arquivo do GanttPRO é de uma torre só e não diz o nome dela. Ela precisa existir na Estrutura da obra."
                        required
                      >
                        <Input
                          id="wizard-tower"
                          list="wizard-towers"
                          value={tower}
                          onChange={(event) => setTower(event.target.value)}
                        />
                      </Field>
                    </>
                  ) : (
                    <p className="text-xs text-(--color-content-muted)">
                      No modelo, cada linha já diz a torre, a disciplina e o serviço.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── 2. Conferência ─────────────────────────────────────────────────── */}
          {step === 1 && file !== undefined && prepared !== undefined && (
            <ImportConferencia
              file={file}
              towerName={tower}
              prepared={prepared}
              choices={drafts}
              onChoice={(key, draft) => setDrafts((current) => ({ ...current, [key]: draft }))}
              onApplyChoices={applyChoices}
              disciplineSuggestions={disciplineNames}
              serviceSuggestions={(discipline) =>
                servicesByDiscipline.get(discipline.trim().toLocaleLowerCase('pt-BR')) ?? []
              }
              preview={preview}
              lines={lines}
              isPreviewing={previewState?.loading === true}
              previewError={previewState?.error}
              options={options}
              onOptions={setOptions}
              declined={effectiveDeclined}
              onApproval={approve}
              onApproveAll={approveAll}
              canCreateServices={canCreateServices}
            />
          )}

          {/* ── 3. Confirmação ─────────────────────────────────────────────────── */}
          {step === 2 && counts !== undefined && preview !== undefined && (
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-medium">Pronto para importar</h3>
              <ul className="flex flex-col gap-1 text-sm">
                <li>
                  <strong>{counts.create}</strong>{' '}
                  {counts.create === 1 ? 'item novo' : 'itens novos'},{' '}
                  <strong>{counts.update}</strong>{' '}
                  {counts.update === 1 ? 'atualizado' : 'atualizados'} e{' '}
                  <strong>{counts.unchanged}</strong> sem mudança.
                </li>
                {options.createMissingFloors && preview.missing.floors.length > 0 && (
                  <li>
                    Vai criar {preview.missing.floors.length}{' '}
                    {preview.missing.floors.length === 1 ? 'pavimento' : 'pavimentos'}.
                  </li>
                )}
                {options.createMissingActivities && preview.missing.activities > 0 && (
                  <li>
                    Vai criar {preview.missing.activities}{' '}
                    {preview.missing.activities === 1 ? 'frente' : 'frentes'}.
                  </li>
                )}
                {options.createMissingDisciplines && preview.missing.disciplines.length > 0 && (
                  <li>
                    Vai criar {preview.missing.disciplines.length}{' '}
                    {preview.missing.disciplines.length === 1 ? 'disciplina' : 'disciplinas'} no
                    catálogo da obra.
                  </li>
                )}
                {options.createMissingServices && approvedServices > 0 && (
                  <li>
                    Vai criar {approvedServices} {approvedServices === 1 ? 'serviço' : 'serviços'}{' '}
                    no catálogo da obra.
                  </li>
                )}
                {counts.error > 0 && (
                  <li className="text-(--color-state-warn)">
                    {counts.error}{' '}
                    {counts.error === 1 ? 'linha com erro fica' : 'linhas com erro ficam'} de fora.
                  </li>
                )}
                {counts.excluded > 0 && (
                  <li className="text-(--color-content-muted)">
                    {counts.excluded}{' '}
                    {counts.excluded === 1
                      ? 'linha de serviço não aprovado fica'
                      : 'linhas de serviços não aprovados ficam'}{' '}
                    de fora.
                  </li>
                )}
              </ul>
              <p className="rounded-md bg-(--color-surface-muted) p-3 text-xs text-(--color-content-muted)">
                A linha de base de cada item novo nasce agora, com as datas do arquivo, e não muda
                mais: reimportar e editar mexem só no previsto (RN-322). O item que o arquivo não
                cita continua como está.
              </p>
              {confirmState.error !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {confirmState.error.message}
                </p>
              )}
            </div>
          )}

          {/* ── 4. Resultado ───────────────────────────────────────────────────── */}
          {step === 3 && result !== undefined && (
            <div className="flex flex-col gap-2 rounded-md border border-(--color-state-ok) bg-(--color-surface-muted) p-4">
              <p className="font-medium">Importação concluída.</p>
              <ul className="text-sm text-(--color-content-muted)">
                <li>{result.created} itens criados</li>
                <li>{result.updated} itens atualizados</li>
                <li>{result.unchanged} sem mudança</li>
                {result.createdFloors > 0 && <li>{result.createdFloors} pavimentos criados</li>}
                {result.createdActivities > 0 && (
                  <li>{result.createdActivities} frentes criadas</li>
                )}
                {result.createdDisciplines > 0 && (
                  <li>{result.createdDisciplines} disciplinas criadas</li>
                )}
                {result.createdServices > 0 && <li>{result.createdServices} serviços criados</li>}
                {result.skipped > 0 && <li>{result.skipped} linhas com erro ficaram de fora</li>}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          {step < 3 && (
            <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
          )}

          {step === 0 && (
            <Button
              type="button"
              variant="primary"
              disabled={file === undefined || !towerReady || reading}
              onClick={() => setStep(1)}
            >
              Conferir
            </Button>
          )}

          {step === 1 && (
            <>
              <Button type="button" variant="secondary" onClick={() => setStep(0)}>
                Voltar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!canConfirm}
                onClick={() => setStep(2)}
              >
                Revisar e confirmar
              </Button>
            </>
          )}

          {step === 2 && counts !== undefined && (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={confirmState.isPending}
                onClick={() => setStep(1)}
              >
                Voltar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={confirmState.isPending || !canConfirm}
                onClick={() => void confirm()}
              >
                {confirmState.isPending
                  ? 'Importando…'
                  : importable > 0
                    ? `Importar ${String(importable)} ${importable === 1 ? 'item' : 'itens'}`
                    : 'Importar'}
              </Button>
            </>
          )}

          {step === 3 && (
            <Button type="button" variant="primary" onClick={() => handleOpenChange(false)}>
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
